import { ApiService } from '@/services/api.service';
import { Config } from '@/config';
import { createGetSSEStream } from '@/util/sse-stream-util';

class OpportunityLlmExtractionsService {
  /**
   * Returns { extraction_id, status, created_at }.
   */
  triggerExtraction({
    opportunityId,
    extractionType = 'fields',
    fieldIds = null,
    evaluationGridTemplateId = null,
    documentIds = null,
    enableTriage = true,
    signal = null,
  }) {
    const body = {
      extractionType,
      fieldIds: fieldIds && fieldIds.length > 0 ? fieldIds : null,
      evaluationGridTemplateId: evaluationGridTemplateId || null,
      documentIds: documentIds && documentIds.length > 0 ? documentIds : null,
      enableTriage,
    };
    return ApiService.post(`${Config.api.document.url}api/v1/opportunities/${opportunityId}/extractions`, body, {
      signal,
    }).then((response) => response.data);
  }

  /**
   * Subscribe to extraction job events from ai-modules via GET SSE.
   *
   * @param {Object} params
   * @param {string} params.jobId - The extraction job ID
   * @param {AbortSignal} [params.signal] - External signal that aborts the stream
   * @param {Object} eventCallbacks - SSE event callbacks
   * @returns {{ abort: Function }} Handle to abort the stream
   */
  subscribeToJob({ jobId, signal = null }, eventCallbacks) {
    const url = `${Config.api.aiModules.url}api/jobs/${jobId}/events`;
    return createGetSSEStream(url, eventCallbacks, { signal });
  }

  /**
   * List the current user's jobs, optionally scoped by opportunity and
   * filtered by job type and a single status; `latest` caps the result to the
   * most recent match. Each job carries `opportunity_id` and
   * `opportunity_name`, so callers can re-attach without knowing the
   * opportunity up front.
   */
  getJobs({ opportunityId = null, jobType = null, status = null, latest = false, signal = null }) {
    const params = [
      opportunityId ? `opportunity=${opportunityId}` : null,
      jobType ? `job_type=${jobType}` : null,
      status ? `status=${status}` : null,
      latest ? 'filter=latest' : null,
    ]
      .filter(Boolean)
      .join('&');
    return ApiService.get(`${Config.api.aiModules.url}api/jobs${params ? `?${params}` : ''}`, {
      // 404 = no jobs yet — not an error.
      validateStatus: (responseStatus) => responseStatus === 200 || responseStatus === 404,
      signal,
    }).then((response) => (response.status === 404 ? [] : response.data || []));
  }

  /**
   * One job by id (status, opportunity, documents used), or null when unknown.
   */
  getJob({ jobId, signal = null }) {
    return ApiService.get(`${Config.api.aiModules.url}api/jobs/${jobId}`, {
      validateStatus: (responseStatus) => responseStatus === 200 || responseStatus === 404,
      signal,
    }).then((response) => (response.status === 404 ? null : response.data || null));
  }

  /**
   * The opportunity's most recent job (of `jobType`, when given), or null.
   */
  getLatestExtraction({ opportunityId, jobType = null, signal = null }) {
    return this.getJobs({ opportunityId, jobType, latest: true, signal }).then((jobs) => jobs[0] || null);
  }
}

export const opportunityLlmExtractionsService = new OpportunityLlmExtractionsService();
